import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { OrchestratorTile } from "@/components/brand/OrchestratorLogo";
import HeaderControls from "@/components/HeaderControls";
import { api } from "@/lib/api";
import i18n from "i18next";
import {
    Sparkles, Check, ArrowRight, ShieldCheck, Radar, GitMerge, Send, Cpu,
    Target, Users, Search, MessageCircle, LineChart, CheckSquare, Edit3, Mic,
    Layers, Quote, Lock, Loader2, Zap, TrendingUp, AlertCircle, ExternalLink,
} from "lucide-react";

function Pill({ children, icon: Icon }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-[var(--jr-surface)] px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--jr-text-2)] font-mono">
            {Icon && <Icon size={12} className="text-[var(--jr-brand)]" />}
            {children}
        </span>
    );
}

function NavBar() {
    const { t } = useTranslation();
    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-[var(--jr-bg)]/85 border-b border-white/8">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
                    <OrchestratorTile size={28} />
                    <span className="font-display text-xl tracking-tight">Orchestrator</span>
                </Link>
                <nav className="hidden md:flex items-center gap-8 text-sm text-[var(--jr-text-2)]">
                    <a href="#hoe" className="hover:text-white transition">{t("landing.howitworks")}</a>
                    <a href="#agents" className="hover:text-white transition">{t("landing.the_agents")}</a>
                    <a href="#match" className="hover:text-white transition">{t("landing.bidir_match")}</a>
                    <a href="#prijzen" className="hover:text-white transition">{t("landing.pricing")}</a>
                    <a href="#compliance" className="hover:text-white transition">{t("landing.eu_ai")}</a>
                </nav>
                <div className="flex items-center gap-3">
                    <HeaderControls variant="landing" className="hidden sm:inline-flex" />
                    <Link to="/login" className="text-sm text-[var(--jr-text-2)] hover:text-white transition" data-testid="nav-login">
                        {t("landing.login")}
                    </Link>
                    <Link to="/register" data-testid="nav-register" className="px-4 py-2 rounded-full text-sm font-medium bg-[var(--jr-brand)] text-white hover:bg-[var(--jr-brand-2)] transition-colors">
                        {t("landing.start_free")}
                    </Link>
                </div>
            </div>
        </header>
    );
}

function Hero() {
    const { t } = useTranslation();
    // Split translated sub-line around the highlight token so we can style it inline.
    const sub = t("hero.sub");
    const hlToken = t("hero.sub_bidir");
    const [pre, post] = sub.includes(hlToken) ? sub.split(hlToken) : [sub, ""];
    return (
        <section className="relative overflow-hidden">
            <div aria-hidden className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[var(--jr-brand)]/10 blur-[140px] pointer-events-none" />
            <div aria-hidden className="absolute inset-0 bg-dotgrid opacity-50 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 sm:pt-28 sm:pb-32">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7 jr-fadein">
                        <Pill icon={Sparkles}>{t("hero.pill")}</Pill>
                        <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-bold leading-[0.95]">
                            {t("hero.title_1")}
                            <br />
                            <span className="text-[var(--jr-brand)]">{t("hero.title_brand")}</span>
                            <br />
                            {t("hero.title_2")}
                        </h1>
                        <p className="mt-7 text-lg text-[var(--jr-text-2)] max-w-xl leading-relaxed">
                            {pre}<span className="text-white font-medium">{hlToken}</span>{post}
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <Link to="/register" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] transition-all glow-brand">
                                {t("hero.cta_primary")} <ArrowRight size={18} />
                            </Link>
                            <a href="#hoe" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/12 text-white hover:bg-white/5 transition-colors" data-testid="hero-cta-secondary">
                                {t("hero.cta_secondary")}
                            </a>
                        </div>
                        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[var(--jr-text-3)]">
                            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[var(--jr-success)]"/> {t("hero.b1")}</span>
                            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[var(--jr-success)]"/> {t("hero.b2")}</span>
                            <span className="inline-flex items-center gap-2"><Check size={14} className="text-[var(--jr-success)]"/> {t("hero.b3")}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-5 jr-fadein" style={{ animationDelay: "0.15s" }}>
                        <HeroAgentPanel />
                    </div>
                </div>
            </div>
        </section>
    );
}

function HeroAgentPanel() {
    const { t } = useTranslation();
    const [active, setActive] = useState(0);
    const steps = [
        { agent: "Signal Scout", status: t("hero.signal_scout_msg"), icon: Radar, color: "#FF5A00" },
        { agent: "Matching Bridge", status: t("hero.matching_msg"), icon: GitMerge, color: "#FF7B33" },
        { agent: "Sales Outreach", status: t("hero.outreach_msg"), icon: Send, color: "#F59E0B" },
        { agent: "Master Sales Agent", status: t("hero.master_msg"), icon: Cpu, color: "#FF5A00" },
    ];
    useEffect(() => {
        const timer = setInterval(() => setActive((i) => (i + 1) % steps.length), 1800);
        return () => clearInterval(timer);
    }, [steps.length]);
    return (
        <div className="relative rounded-2xl border border-white/10 bg-[var(--jr-surface)]/80 backdrop-blur-xl p-5 shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-white/8">
                <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[var(--jr-brand)] jr-pulse" />
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--jr-text-2)]">{t("hero.live_orch")}</span>
                </div>
                <span className="font-mono text-xs text-[var(--jr-text-3)]">{t("hero.workspace_demo")}</span>
            </div>
            <div className="mt-4 space-y-3">
                {steps.map((s, i) => {
                    const Icon = s.icon;
                    const isActive = i === active;
                    return (
                        <div key={s.agent} className={`flex items-start gap-3 rounded-lg p-3 transition-all duration-300 ${isActive ? "bg-white/5 border-l-2 border-[var(--jr-brand)] pl-[10px]" : "border-l-2 border-transparent pl-[10px]"}`}>
                            <div className={`mt-0.5 w-7 h-7 rounded-md grid place-items-center transition-all ${isActive ? "bg-[var(--jr-brand)]/15" : "bg-white/5"}`}>
                                <Icon size={14} style={{ color: isActive ? s.color : "var(--jr-text-2)" }} />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-white">{s.agent}</span>
                                    {isActive && (
                                        <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--jr-brand)] font-mono">{t("hero.running")}</span>
                                    )}
                                </div>
                                <p className="text-xs text-[var(--jr-text-2)] mt-0.5">{s.status}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="mt-5 pt-4 border-t border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-[var(--jr-success)]" />
                    <span className="text-xs text-[var(--jr-text-2)]">{t("hero.approval_required")}</span>
                </div>
                <button className="px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-white text-xs font-medium hover:bg-[var(--jr-brand-2)] transition-colors">
                    {t("hero.approve")}
                </button>
            </div>
        </div>
    );
}

function TrustStrip() {
    const { t } = useTranslation();
    const items = ["VINTRA MOBILITY", "NORTHWIND PHARMA", "HELIX ROBOTICS", "AURORA FINTRACK",
                   "KESTRELOPS", "MOSAIC LOGISTICS", "VERITAS LEGAL", "CINDER ENERGY"];
    const dup = [...items, ...items];
    return (
        <section className="border-y border-white/8 py-8 overflow-hidden bg-[var(--jr-bg-2)]">
            <div className="text-center text-xs uppercase tracking-[0.3em] text-[var(--jr-text-3)] mb-6">
                {t("trust.line")}
            </div>
            <div className="relative w-full overflow-hidden">
                <div className="flex gap-12 jr-marquee min-w-max">
                    {dup.map((c, i) => (
                        <span key={i} className="font-display text-xl text-[var(--jr-text-3)] whitespace-nowrap">{c}</span>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    const { t } = useTranslation();
    const cards = [
        { n: "01", title: t("how.c1_title"), body: t("how.c1_body"), icon: Target },
        { n: "02", title: t("how.c2_title"), body: t("how.c2_body"), icon: Cpu },
        { n: "03", title: t("how.c3_title"), body: t("how.c3_body"), icon: ShieldCheck },
    ];
    return (
        <section id="hoe" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-6">
                        <Pill icon={Layers}>{t("how.pill")}</Pill>
                        <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight">
                            {t("how.title_1")}<br /> {t("how.title_2")}
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-[var(--jr-text-2)] text-lg leading-relaxed">
                        {t("how.sub")}
                    </p>
                </div>
                <div className="mt-16 grid md:grid-cols-3 gap-6">
                    {cards.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <div key={i} className="group rounded-xl border border-white/8 bg-[var(--jr-surface)] p-7 hover:-translate-y-[2px] transition-transform">
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-xs text-[var(--jr-text-3)] tracking-[0.2em]">{c.n}</span>
                                    <Icon size={20} className="text-[var(--jr-brand)]" />
                                </div>
                                <h3 className="font-display mt-6 text-2xl tracking-tight font-medium">{c.title}</h3>
                                <p className="mt-3 text-[var(--jr-text-2)]">{c.body}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function AgentsSection() {
    const { t } = useTranslation();
    const agents = [
        { name: t("agents_section.a1_name"), role: t("agents_section.a1_role"), icon: Target, master: true },
        { name: t("agents_section.a2_name"), role: t("agents_section.a2_role"), icon: Users, master: true },
        { name: t("agents_section.a3_name"), role: t("agents_section.a3_role"), icon: Radar },
        { name: t("agents_section.a4_name"), role: t("agents_section.a4_role"), icon: Search },
        { name: t("agents_section.a5_name"), role: t("agents_section.a5_role"), icon: Send },
        { name: t("agents_section.a6_name"), role: t("agents_section.a6_role"), icon: MessageCircle },
        { name: t("agents_section.a7_name"), role: t("agents_section.a7_role"), icon: LineChart },
        { name: t("agents_section.a8_name"), role: t("agents_section.a8_role"), icon: CheckSquare },
        { name: t("agents_section.a9_name"), role: t("agents_section.a9_role"), icon: GitMerge, bridge: true },
        { name: t("agents_section.a10_name"), role: t("agents_section.a10_role"), icon: Edit3 },
        { name: t("agents_section.a11_name"), role: t("agents_section.a11_role"), icon: Mic },
    ];
    return (
        <section id="agents" className="relative py-24 sm:py-32 border-t border-white/8 bg-[var(--jr-bg-2)]">
            <div aria-hidden className="absolute inset-0 bg-dotgrid opacity-40" />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <Pill icon={Cpu}>{t("agents_section.pill")}</Pill>
                <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-semibold max-w-2xl leading-tight">
                    {t("agents_section.title")}
                </h2>
                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {agents.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <div key={i} className={`group rounded-xl border p-5 transition-all hover:-translate-y-[1px] ${
                                a.master ? "border-[var(--jr-brand)]/40 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent"
                                : a.bridge ? "border-[var(--jr-amber)]/30 bg-[var(--jr-surface)]"
                                : "border-white/8 bg-[var(--jr-surface)] hover:border-white/14"
                            }`}>
                                <div className="flex items-center justify-between">
                                    <Icon size={18} className={a.master ? "text-[var(--jr-brand)]" : a.bridge ? "text-[var(--jr-amber)]" : "text-white"} />
                                    {a.master && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-brand)]">{t("agents_section.master_label")}</span>}
                                    {a.bridge && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-amber)]">{t("agents_section.bridge_label")}</span>}
                                </div>
                                <h3 className="font-display mt-4 text-lg tracking-tight">{a.name}</h3>
                                <p className="text-sm text-[var(--jr-text-2)] mt-1">{a.role}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function KillerMatch() {
    const { t } = useTranslation();
    const sub = t("match_section.sub");
    const hl = t("match_section.sub_highlight");
    const [pre, post] = sub.includes(hl) ? sub.split(hl) : [sub, ""];
    return (
        <section id="match" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <Pill icon={GitMerge}>{t("match_section.pill")}</Pill>
                        <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight">
                            {t("match_section.title_1")}<br />{t("match_section.title_2")}
                        </h2>
                        <p className="mt-6 text-lg text-[var(--jr-text-2)] leading-relaxed">
                            {pre}<span className="text-white">{hl}</span>{post}
                        </p>
                        <ul className="mt-7 space-y-3 text-[var(--jr-text-2)]">
                            <li className="flex gap-3"><Check className="text-[var(--jr-brand)] mt-0.5" size={18}/>{t("match_section.b1")}</li>
                            <li className="flex gap-3"><Check className="text-[var(--jr-brand)] mt-0.5" size={18}/>{t("match_section.b2")}</li>
                            <li className="flex gap-3"><Check className="text-[var(--jr-brand)] mt-0.5" size={18}/>{t("match_section.b3")}</li>
                        </ul>
                    </div>
                    <div className="lg:col-span-7">
                        <MatchVisual />
                    </div>
                </div>
            </div>
        </section>
    );
}

function MatchVisual() {
    const { t } = useTranslation();
    return (
        <div className="rounded-2xl border border-white/10 bg-[var(--jr-surface)] p-6">
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--jr-text-3)] mb-3">{t("match_section.signal_label")}</div>
                    <div className="rounded-lg border border-white/10 p-4 bg-[var(--jr-surface-2)]">
                        <div className="flex items-center justify-between">
                            <span className="font-display text-lg">Vintra Mobility</span>
                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)]">Score 94</span>
                        </div>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">{t("match_section.signal_meta")}</p>
                        <div className="mt-3 rounded bg-[var(--jr-brand)]/10 border border-[var(--jr-brand)]/30 px-2 py-1 text-xs text-[var(--jr-brand)] inline-block">
                            Series A €12M
                        </div>
                        <p className="text-sm text-[var(--jr-text-2)] mt-3 leading-relaxed">
                            {t("match_section.signal_body")}
                        </p>
                    </div>
                </div>
                <div>
                    <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--jr-text-3)] mb-3 text-right">{t("match_section.talent_label")}</div>
                    <div className="space-y-2">
                        {[
                            { name: "Lara Devos", role: "Senior Backend Engineer", conf: 92 },
                            { name: "Sofie Maes", role: "Staff ML Engineer", conf: 89 },
                            { name: "Tariq El Ouali", role: "AE Enterprise", conf: 84 },
                        ].map((c) => (
                            <div key={c.name} className="rounded-lg border border-white/10 p-3 bg-[var(--jr-surface-2)] flex items-center justify-between hover:border-[var(--jr-brand)]/30 transition-colors">
                                <div>
                                    <div className="text-sm font-medium text-white">{c.name}</div>
                                    <div className="text-xs text-[var(--jr-text-2)]">{c.role}</div>
                                </div>
                                <span className="text-[10px] font-mono tracking-[0.16em] text-[var(--jr-brand)]">{c.conf}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/8 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-[var(--jr-text-2)]">
                    <GitMerge size={14} className="text-[var(--jr-amber)]"/> {t("match_section.matching_footer")}
                </div>
                <button className="px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-white text-xs font-medium hover:bg-[var(--jr-brand-2)] transition-colors">
                    {t("match_section.joint_outreach")}
                </button>
            </div>
        </div>
    );
}

function Personas() {
    const { t } = useTranslation();
    const personas = [
        { name: t("personas_section.p1_name"), quote: t("personas_section.p1_quote"), need: t("personas_section.p1_need"),
            avatar: "https://images.pexels.com/photos/31869537/pexels-photo-31869537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
        { name: t("personas_section.p2_name"), quote: t("personas_section.p2_quote"), need: t("personas_section.p2_need"),
            avatar: "https://images.unsplash.com/photo-1758534063829-a72058381e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MTk5NTA5Mnww&ixlib=rb-4.1.0&q=85" },
        { name: t("personas_section.p3_name"), quote: t("personas_section.p3_quote"), need: t("personas_section.p3_need"),
            avatar: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MTk5NTA5Mnww&ixlib=rb-4.1.0&q=85" },
    ];
    return (
        <section className="py-24 sm:py-32 border-t border-white/8 bg-[var(--jr-bg-2)]">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <Pill>{t("personas_section.pill")}</Pill>
                <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-semibold max-w-3xl leading-tight">
                    {t("personas_section.title")}
                </h2>
                <div className="mt-14 grid md:grid-cols-3 gap-6">
                    {personas.map((p, i) => (
                        <div key={i} className="rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6 hover:border-white/14 transition-colors">
                            <div className="flex items-center gap-3">
                                <img src={p.avatar} alt={p.name} className="w-12 h-12 rounded-full object-cover border border-white/10"/>
                                <div>
                                    <div className="font-display text-lg tracking-tight">{p.name}</div>
                                    <div className="text-xs text-[var(--jr-brand)] font-mono uppercase tracking-[0.16em]">{p.need}</div>
                                </div>
                            </div>
                            <div className="mt-5 relative">
                                <Quote className="absolute -left-1 -top-1 text-[var(--jr-brand)]/40" size={20}/>
                                <p className="pl-7 text-[var(--jr-text-2)] leading-relaxed italic">{p.quote}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Pricing() {
    const { t } = useTranslation();
    const tiers = [
        { id: "solo", name: t("pricing_section.t1_name"), price: "€99", credits: t("pricing_section.t1_credits"),
            features: [t("pricing_section.t1_f1"), t("pricing_section.t1_f2"), t("pricing_section.t1_f3"),
                       t("pricing_section.t1_f4"), t("pricing_section.t1_f5"), t("pricing_section.t1_f6")],
            cta: t("pricing_section.t1_cta"), highlight: false },
        { id: "team", name: t("pricing_section.t2_name"), price: "€399", credits: t("pricing_section.t2_credits"),
            features: [t("pricing_section.t2_f1"), t("pricing_section.t2_f2"), t("pricing_section.t2_f3"),
                       t("pricing_section.t2_f4"), t("pricing_section.t2_f5"), t("pricing_section.t2_f6")],
            cta: t("pricing_section.t2_cta"), highlight: true },
        { id: "agency", name: t("pricing_section.t3_name"), price: "€1.299", credits: t("pricing_section.t3_credits"),
            features: [t("pricing_section.t3_f1"), t("pricing_section.t3_f2"), t("pricing_section.t3_f3"),
                       t("pricing_section.t3_f4"), t("pricing_section.t3_f5"), t("pricing_section.t3_f6")],
            cta: t("pricing_section.t3_cta"), highlight: false },
    ];
    return (
        <section id="prijzen" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <Pill>{t("pricing_section.pill")}</Pill>
                        <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-semibold leading-tight">
                            {t("pricing_section.title_1")}<br />{t("pricing_section.title_2")}
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-[var(--jr-text-2)]">
                        {t("pricing_section.sub")}
                    </p>
                </div>
                <div className="mt-14 grid md:grid-cols-3 gap-6">
                    {tiers.map((ti) => (
                        <div key={ti.id} className={`rounded-2xl p-7 flex flex-col ${
                            ti.highlight ? "border-2 border-[var(--jr-brand)] bg-[var(--jr-surface)] glow-brand"
                            : "border border-white/8 bg-[var(--jr-surface)]"
                        }`} data-testid={`pricing-tier-${ti.id}`}>
                            <div className="flex items-center justify-between">
                                <h3 className="font-display text-2xl tracking-tight">{ti.name}</h3>
                                {ti.highlight && (
                                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)] bg-[var(--jr-brand)]/10 px-2 py-1 rounded">{t("pricing_section.popular")}</span>
                                )}
                            </div>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-display text-5xl tracking-tighter">{ti.price}</span>
                                <span className="text-[var(--jr-text-2)]">{t("pricing_section.per_month")}</span>
                            </div>
                            <p className="text-sm text-[var(--jr-text-2)] mt-1">{ti.credits}</p>
                            <ul className="mt-8 space-y-3 text-sm text-[var(--jr-text-2)] flex-1">
                                {ti.features.map((f, i) => (
                                    <li key={i} className="flex gap-2">
                                        <Check size={16} className="mt-0.5 text-[var(--jr-brand)] shrink-0"/> <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/register" data-testid={`pricing-cta-${ti.id}`}
                                className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-medium transition-colors ${
                                    ti.highlight ? "bg-[var(--jr-brand)] text-white hover:bg-[var(--jr-brand-2)]"
                                    : "border border-white/14 text-white hover:bg-white/5"
                                }`}>
                                {ti.cta} <ArrowRight size={16}/>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Compliance() {
    const { t } = useTranslation();
    return (
        <section id="compliance" className="py-20 border-t border-white/8 bg-[var(--jr-bg-2)]">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5">
                        <Pill icon={Lock}>{t("compliance_section.pill")}</Pill>
                        <h2 className="font-display mt-5 text-3xl sm:text-4xl tracking-tight font-semibold">{t("compliance_section.title")}</h2>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {[
                            [t("compliance_section.c1_title"), t("compliance_section.c1_body")],
                            [t("compliance_section.c2_title"), t("compliance_section.c2_body")],
                            [t("compliance_section.c3_title"), t("compliance_section.c3_body")],
                            [t("compliance_section.c4_title"), t("compliance_section.c4_body")],
                        ].map(([h, b]) => (
                            <div key={h} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
                                <div className="text-sm font-medium text-white">{h}</div>
                                <p className="text-sm text-[var(--jr-text-2)] mt-1">{b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    const { t } = useTranslation();
    return (
        <section className="py-24 sm:py-32 relative overflow-hidden">
            <div aria-hidden className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[var(--jr-brand)]/10 blur-[140px] pointer-events-none" />
            <div className="relative max-w-4xl mx-auto px-6 text-center">
                <h2 className="font-display text-4xl sm:text-6xl tracking-tighter font-bold leading-[1]">
                    {t("final_cta.title")}
                </h2>
                <p className="mt-6 text-lg text-[var(--jr-text-2)] max-w-2xl mx-auto">
                    {t("final_cta.sub")}
                </p>
                <div className="mt-9 flex items-center justify-center gap-3">
                    <Link to="/register" data-testid="final-cta" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--jr-brand)] text-white text-lg font-medium hover:bg-[var(--jr-brand-2)] transition-colors glow-brand">
                        {t("final_cta.cta")} <ArrowRight size={18}/>
                    </Link>
                </div>
            </div>
        </section>
    );
}

function Footer() {
    const { t } = useTranslation();
    return (
        <footer className="border-t border-white/8 py-12 bg-[var(--jr-bg-2)]">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-6">
                <div className="flex items-center gap-2">
                    <OrchestratorTile size={28} />
                    <span className="font-display text-lg tracking-tight">Orchestrator</span>
                </div>
                <p className="text-xs text-[var(--jr-text-3)] font-mono uppercase tracking-[0.2em]">
                    {t("footer.copy")}
                </p>
                <div className="flex gap-6 text-sm text-[var(--jr-text-2)]">
                    <a href="#">{t("footer.privacy")}</a>
                    <a href="#">{t("footer.terms")}</a>
                    <a href="#">{t("footer.security")}</a>
                </div>
            </div>
        </footer>
    );
}


/* ============================================================
 *  Agent-Diagnose · landing conversion widget
 *  1 Serper-signaal zichtbaar + 2 geblurred + CTA naar register
 * ============================================================ */
function AgentDiagnose() {
    const [company, setCompany] = useState("");
    const [url, setUrl] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null); // {total_found, full, blurred, cta_teaser}
    const [step, setStep] = useState(0);        // fake stepper during load

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (company.trim().length < 2) return setError(i18n.t("landing.diag_err_company"));
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setError(i18n.t("landing.diag_err_email"));
        setBusy(true); setStep(0); setResult(null);
        // Fake stepper so the perceived wait is 'agentic'
        const t1 = setTimeout(() => setStep(1), 500);
        const t2 = setTimeout(() => setStep(2), 1300);
        const t3 = setTimeout(() => setStep(3), 2300);
        try {
            const { data } = await api.post("/public/agent-diagnose", {
                company: company.trim(),
                url: url.trim() || null,
                email: email.trim().toLowerCase(),
                locale: (document.documentElement.lang || "nl").slice(0, 2),
            });
            setResult(data);
        } catch (e2) {
            const msg = e2?.response?.data?.detail || "Er ging iets mis. Probeer opnieuw.";
            setError(msg);
        } finally {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            setBusy(false); setStep(4);
        }
    };

    return (
        <section id="diagnose" className="relative py-20 sm:py-28 border-t border-white/8 bg-[var(--jr-bg-2)]" data-testid="agent-diagnose-section">
            <div className="max-w-6xl mx-auto px-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div className="md:max-w-2xl">
                        <Pill icon={Zap}>{i18n.t("landing.diag_pill")}</Pill>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
                            {i18n.t("landing.diag_h_pre")} <span className="text-[var(--jr-brand)]">{i18n.t("landing.diag_h_mid")}</span> {i18n.t("landing.diag_h_post")}
                        </h2>
                        <p className="mt-4 text-[var(--jr-text-2)] leading-relaxed">
                            {i18n.t("landing.diag_p_pre")} <strong className="text-white">{i18n.t("landing.diag_p_strong")}</strong> {i18n.t("landing.diag_p_post")}
                            {i18n.t("landing.diag_p2")}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-mono text-[var(--jr-text-3)]">
                        <div className="flex items-center gap-2"><Radar size={12} className="text-[var(--jr-brand)]"/> Serper.dev</div>
                        <div className="w-px h-4 bg-white/10"/>
                        <div className="flex items-center gap-2"><ShieldCheck size={12} className="text-emerald-400"/> GDPR-safe</div>
                    </div>
                </div>

                {/* Card */}
                <div className="relative rounded-3xl border border-white/10 bg-[var(--jr-surface)] overflow-hidden">
                    {/* subtle gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[var(--jr-brand)]/[0.03] via-transparent to-transparent pointer-events-none"/>

                    {/* Form */}
                    <form onSubmit={submit} className="relative p-6 sm:p-10 grid gap-4 sm:grid-cols-[1fr_1fr_1fr_auto]" data-testid="agent-diagnose-form">
                        <div className="sm:col-span-1">
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("landing.diag_lbl_company")}</label>
                            <input value={company} onChange={(e) => setCompany(e.target.value)}
                                data-testid="diagnose-company" placeholder="Aikido Security"
                                className="mt-1 w-full bg-[var(--jr-bg)] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[var(--jr-brand)]"/>
                        </div>
                        <div className="sm:col-span-1">
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("landing.diag_lbl_url")}</label>
                            <input value={url} onChange={(e) => setUrl(e.target.value)}
                                data-testid="diagnose-url" placeholder="https://aikido.dev"
                                className="mt-1 w-full bg-[var(--jr-bg)] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[var(--jr-brand)]"/>
                        </div>
                        <div className="sm:col-span-1">
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{i18n.t("landing.diag_lbl_email")}</label>
                            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                                data-testid="diagnose-email" placeholder="jij@bedrijf.com"
                                className="mt-1 w-full bg-[var(--jr-bg)] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[var(--jr-brand)]"/>
                        </div>
                        <div className="sm:col-span-1 flex items-end">
                            <button type="submit" disabled={busy}
                                data-testid="diagnose-submit"
                                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60 transition-colors glow-brand">
                                {busy ? <Loader2 className="animate-spin" size={16}/> : <Zap size={16}/>}
                                {busy ? "Analyseren…" : "Analyseer nu"}
                            </button>
                        </div>
                    </form>

                    {/* Error */}
                    {error && (
                        <div className="mx-6 sm:mx-10 mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 flex items-center gap-2" data-testid="diagnose-error">
                            <AlertCircle size={14} className="text-red-400"/>
                            <span className="text-sm text-red-300">{error}</span>
                        </div>
                    )}

                    {/* Loading stepper (agentic feel) */}
                    {busy && (
                        <div className="mx-6 sm:mx-10 mb-8 space-y-2" data-testid="diagnose-progress">
                            <ProgressStep active={step >= 0} done={step > 0} label={i18n.t("landing.diag_step1")}/>
                            <ProgressStep active={step >= 1} done={step > 1} label={i18n.t("landing.diag_step2")}/>
                            <ProgressStep active={step >= 2} done={step > 2} label={i18n.t("landing.diag_step3")}/>
                            <ProgressStep active={step >= 3} done={step > 3} label={i18n.t("landing.diag_step4")}/>
                        </div>
                    )}

                    {/* Result */}
                    {result && !busy && (
                        <div className="mx-6 sm:mx-10 mb-10 space-y-4" data-testid="diagnose-result">
                            {result.total_found === 0 ? (
                                <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                                    <p className="text-sm text-[var(--jr-text-2)]">
                                        Geen fresh signalen gevonden voor <strong className="text-white">{company}</strong>.
                                        Grote bedrijven met minder publieke news-hits vergen een diepere scan — die krijg je in de trial.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Header count */}
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-mono uppercase tracking-[0.16em]" data-testid="diagnose-count">
                                            <TrendingUp size={12}/> {result.total_found} signalen gevonden
                                        </div>
                                        <div className="text-xs text-[var(--jr-text-3)] font-mono">
                                            → 1 zichtbaar · {Math.max(0, result.total_found - 1)} achter de paywall
                                        </div>
                                    </div>

                                    {/* Full signal card */}
                                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.03] p-5 sm:p-6" data-testid="diagnose-full-signal">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-2 flex items-center gap-2">
                                                    <Radar size={10}/> Signaal · {result.full.signal_type} · GRATIS
                                                </div>
                                                <h3 className="font-display text-xl text-white leading-snug">
                                                    {result.full.signal_title}
                                                </h3>
                                                {result.full.signal_detail && (
                                                    <p className="mt-3 text-sm text-[var(--jr-text-2)] leading-relaxed">
                                                        {result.full.signal_detail}
                                                    </p>
                                                )}
                                                {result.full.source_urls?.[0] && (
                                                    <a href={result.full.source_urls[0]} target="_blank" rel="noopener noreferrer"
                                                        className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--jr-brand)] hover:underline">
                                                        <ExternalLink size={12}/> Bekijk bron
                                                    </a>
                                                )}
                                            </div>
                                            {result.full.date && (
                                                <div className="text-[10px] font-mono text-[var(--jr-text-3)] whitespace-nowrap">
                                                    {result.full.date}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Blurred signals */}
                                    {(result.blurred || []).map((s, i) => (
                                        <div key={i} className="relative rounded-2xl border border-white/10 bg-[var(--jr-bg)] p-5 sm:p-6 overflow-hidden" data-testid={`diagnose-blurred-${i}`}>
                                            <div className="absolute inset-0 pointer-events-none" style={{
                                                background: "linear-gradient(90deg, transparent 0%, transparent 30%, var(--jr-bg-2) 100%)",
                                            }}/>
                                            <div className="relative z-10 blur-[3px] select-none">
                                                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--jr-text-3)] mb-2 flex items-center gap-2">
                                                    <Radar size={10}/> Signaal · {s.signal_type || "•••"}
                                                </div>
                                                <h3 className="font-display text-xl text-white leading-snug">{s.signal_title}</h3>
                                                <p className="mt-3 text-sm text-[var(--jr-text-2)]">
                                                    ••••••••• ••••• •••• ••••••••• •••••• ••••• •••••• ••• •••••••••••••••
                                                </p>
                                            </div>
                                            <div className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--jr-brand)]/15 border border-[var(--jr-brand)]/30 text-[var(--jr-brand)] text-[10px] font-mono uppercase tracking-[0.16em]">
                                                <Lock size={10}/> Ontgrendel
                                            </div>
                                        </div>
                                    ))}

                                    {/* CTA */}
                                    <div className="mt-2 rounded-2xl border border-[var(--jr-brand)]/30 bg-gradient-to-r from-[var(--jr-brand)]/[0.08] to-transparent p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                                        <div>
                                            <div className="font-display text-lg text-white">
                                                {result.cta_teaser}
                                            </div>
                                            <div className="mt-1 text-xs text-[var(--jr-text-3)]">
                                                Geen credit card · volledige toegang tot alle signalen + decision makers + auto-outreach drafts.
                                            </div>
                                        </div>
                                        <Link to={`/register?prefill_email=${encodeURIComponent(email)}&prefill_company=${encodeURIComponent(company)}`}
                                            data-testid="diagnose-register-cta"
                                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] whitespace-nowrap">
                                            Start gratis trial <ArrowRight size={16}/>
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Bottom-line */}
                <p className="mt-6 text-center text-xs text-[var(--jr-text-3)] font-mono">
                    Ons trust-DNA: max 3 diagnoses per e-mail per dag · geen spam · uitschrijven per klik.
                </p>
            </div>
        </section>
    );
}

function ProgressStep({ active, done, label }) {
    return (
        <div className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${active ? "bg-[var(--jr-brand)]/[0.06] border border-[var(--jr-brand)]/25" : "border border-transparent"}`}>
            {done ? (
                <Check size={14} className="text-emerald-400"/>
            ) : active ? (
                <Loader2 className="animate-spin text-[var(--jr-brand)]" size={14}/>
            ) : (
                <div className="w-3 h-3 rounded-full border border-white/20"/>
            )}
            <span className={done ? "text-white" : active ? "text-white" : "text-[var(--jr-text-3)]"}>{label}</span>
        </div>
    );
}


export default function Landing() {
    return (
        <div>
            <NavBar />
            <Hero />
            <AgentDiagnose />
            <TrustStrip />
            <HowItWorks />
            <AgentsSection />
            <KillerMatch />
            <Personas />
            <Pricing />
            <Compliance />
            <FinalCTA />
            <Footer />
        </div>
    );
}
